import { FC, ReactNode } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { QcsLoadingButton } from '../../../common/basic/QcsLoadingButton';
import { QcsTooltip } from '../../../common/basic/QcsTooltip';
import { DocumentPreviewModal } from '../../../common/DocumentPreviewModal';
import { useDocumentPreview } from '../../../../hooks/useDocumentPreview';

interface Props {
  tooltipTitle?: string;
  icon?: ReactNode;
  disabled?: boolean;
  documentUrl: string;
  color?: 'inherit' | 'action' | 'disabled' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  variant?: 'text' | 'outlined' | 'contained';
  children?: ReactNode;
  downloadOnly?: boolean;
  fileName?: string;
  fetchDocument?: () => Promise<{ data: Blob; headers: { 'content-type': string } }>;
  silentError?: boolean;
  sx?: object;
}

export const ShowImageButton: FC<Props> = ({
  tooltipTitle,
  icon,
  disabled = false,
  documentUrl,
  color,
  variant,
  children,
  downloadOnly = false,
  fileName,
  fetchDocument,
  silentError = false,
  sx,
}) => {
  const preview = useDocumentPreview();

  const handleClick = async () => {
    if (downloadOnly) {
      const link = document.createElement('a');
      link.href = documentUrl;
      link.target = '_blank';
      link.download = fileName || 'document';
      link.click();
      return;
    }

    await preview.open(documentUrl, { fileName, fetchDocument, silentError });
  };

  return (
    <>
      {!downloadOnly && (
        <DocumentPreviewModal
          iframeSrc={preview.iframeSrc}
          contentType={preview.contentType}
          onClose={preview.close}
          onDownload={preview.download}
        />
      )}

      <QcsTooltip title={tooltipTitle} placement="top">
        <span style={{ display: 'inline-block' }}>
          <QcsLoadingButton onClick={handleClick} disabled={disabled} loading={preview.isLoading} variant={variant} sx={{ width: 'max-content', ...sx }}>
            {children ?? icon ?? <VisibilityIcon color={disabled ? undefined : color ?? 'primary'} />}
          </QcsLoadingButton>
        </span>
      </QcsTooltip>
    </>
  );
};
